<template>
    <div
        :id="`${id}-address-container`"
        class="tl-address-container"
        data-testid="address-container"
    >
        <DefineSearch>
            <div class="tl-address-container-search">
                <InputAutocomplete
                    :id="`${id}-search`"
                    v-model="searchQuery"
                    :label="t('search')"
                    :disabled="disabled"
                    :items="suggestions"
                    :item-label="addressSuggestionText"
                    :options-loading="suggestionsLoading"
                    :no-results-eligible="searchCompleted"
                    @search="handleSearchInput"
                    @select="selectAddressSuggestion"
                />
            </div>
        </DefineSearch>

        <Words
            v-if="label"
            class="tl-address-container-label"
        >{{ label }}</Words>

        <ReuseSearch v-if="autocompleteEnabled !== false && !searchAfterBlockId" />

        <div
            class="container-block tl-address-container-fields"
            :data-index-path="indexPath"
        >
            <template
                v-for="child in children"
                :key="child.id"
            >
                <!-- one element with the child's index path, so the builder drags them together and drops nothing between them -->
                <div
                    v-if="child.id === searchAfterBlockId"
                    class="tl-address-container-with-search"
                    :data-index-path="child.indexPath"
                >
                    <CustomPageBlock v-bind="blockProps(child)" />
                    <ReuseSearch />
                </div>
                <CustomPageBlock
                    v-else
                    v-bind="blockProps(child)"
                />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { createReusableTemplate } from '@vueuse/core'
import { kebabCase } from 'change-case'
import { useAddressSuggestions } from 'widget-components/composables/address-suggestions'
import InputAutocomplete from 'widget-components/components/InputAutocomplete/InputAutocomplete.vue'
import Words from 'widget-components/components/Words/Words.vue'
import { CustomPageBlock } from 'widget-components/integrations/CustomPage'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { CPInputConfiguration, ExtendedBlock, InputCountry, InputCountryItem, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import { emitBlockUpdate } from 'widget-components/integrations/CustomPage/utilities/emitBlockUpdate'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { useI18n } from 'widget-components/utilities/i18n'
import { addressSuggestionText, type AddressKey, type AddressSuggestion, type SearchPosition } from './types'

defineOptions({ inheritAttrs: false })

// a missing boolean prop would otherwise be cast to false, and missing autocompleteEnabled means on
const props = withDefaults(defineProps<{
    id: string
    children: ExtendedBlock[]
    label?: string
    addressFields?: Record<string, AddressKey>
    autocompleteEnabled?: boolean | null
    searchPosition?: SearchPosition | null
    disabled?: boolean
    indexPath?: number[]
}>(), {
    autocompleteEnabled: true,
})

const { getLazyBlockProp, requestBlockConfig } = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {},
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const { t } = useI18n()

const [DefineSearch, ReuseSearch] = createReusableTemplate()

const countryChild = computed(() => flattenBlocks(props.children).find(child => props.addressFields?.[child.id] === 'countryCode'))

const country = computed(() => (countryChild.value?.currentValue as InputCountryItem | undefined)?.countryCode ?? null)

// the search goes after the child holding the country, the country itself or its row; without a country child it goes on top,
// so the preview of an unfinished container still shows it
const searchAfterBlockId = computed(() => props.autocompleteEnabled !== false && props.searchPosition === 'AFTER_COUNTRY'
    ? props.children.find(child => flattenBlocks([child]).some(block => block.id === countryChild.value?.id))?.id
    : undefined)

const blockProps = (child: ExtendedBlock) => ({
    block: child,
    modelValue: child.currentValue,
    'data-testid': `custom-page-block-${kebabCase((child as ExtendedBlock<CPInputConfiguration>).fieldName || child.type)}`,
})

const {
    searchQuery,
    suggestions,
    suggestionsLoading,
    searchCompleted,
    handleSearchInput,
    clearSearch,
} = useAddressSuggestions<AddressSuggestion>(() => props.id, () => country.value)

const selectCountry = async (child: ExtendedBlock, countryCode: string) => {
    await requestBlockConfig({ id: child.id, type: 'INPUT_COUNTRY' })

    const lazyItems = getLazyBlockProp<InputCountryItem[] | undefined>(child.id, 'items')
    const items = lazyItems?.length ? lazyItems : ((child as ExtendedBlock<InputCountry>).items ?? [])
    const item = items.find(i => i.countryCode === countryCode)

    if (item) {
        emitBlockUpdate(child.blockPath, { id: child.id, error: undefined, currentValue: item })
    }
}

const selectAddressSuggestion = async (suggestion: AddressSuggestion) => {
    clearSearch()

    // children are resolved by instance id, so in a repeatable container each section fills only its own
    flattenBlocks(props.children).forEach(child => {
        const role = props.addressFields?.[child.id]

        if (role && role !== 'countryCode') {
            // a part the suggestion lacks is cleared, so nothing of the previous address is left behind
            emitBlockUpdate(child.blockPath, { id: child.id, error: undefined, currentValue: suggestion[role] ?? '' })
        }
    })

    if (countryChild.value && suggestion.countryCode) {
        await selectCountry(countryChild.value, suggestion.countryCode)
    }
}
</script>

<style scoped lang="sass">
.tl-address-container
    display: flex
    flex-direction: column
    gap: var(--tl-address-container-gap, 16px)

    &-label
        display: block
        margin-top: var(--tl-cp-block-margin)

    &-fields,
    &-with-search
        display: flex
        flex-direction: column
        gap: var(--tl-address-container-gap, 16px)
</style>
